// src/components/admin/AdminLogin.tsx
import React, { useEffect, useState } from "react";
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Stack,
  Alert,
} from "@mui/material";
import { useNavigate } from "react-router-dom";

const ADMIN_LOGIN_STORAGE_KEY = "okm_admin_logged_in";
const ADMIN_LAST_ACCESS_KEY = "okm_admin_last_access";
const ADMIN_TIMEOUT_MS = 24 * 60 * 60 * 1000; // 24時間

const AdminLogin: React.FC = () => {
  const navigate = useNavigate();

  const [id, setId] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // すでにログイン済み & 24時間以内なら /admin へ飛ばす
  useEffect(() => {
    try {
      const logged = localStorage.getItem(ADMIN_LOGIN_STORAGE_KEY) === "1";
      const last = localStorage.getItem(ADMIN_LAST_ACCESS_KEY);
      if (logged && last) {
        const diff = Date.now() - Number(last);
        if (!Number.isNaN(diff) && diff < ADMIN_TIMEOUT_MS) {
          navigate("/admin", { replace: true });
        } else {
          // タイムアウト → ローカルストレージをクリア
          localStorage.removeItem(ADMIN_LOGIN_STORAGE_KEY);
          localStorage.removeItem(ADMIN_LAST_ACCESS_KEY);
        }
      }
    } catch {
      // localStorage 触れない環境でも落ちないように握りつぶす
    }
  }, [navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!id || !password) {
      setError("ID とパスワードを入力してください。");
      return;
    }

    setSubmitting(true);
    try {
      const res = await fetch("/api/admin/login", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ id, password }),
      });

      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        console.error("/api/admin/login failed:", data);
        setError("ID またはパスワードが正しくありません。");
        return;
      }

      const data = await res.json();
      if (!data.ok) {
        setError("管理者認証に失敗しました。");
        return;
      }

      // ログイン成功 → ローカルストレージにフラグを保存
      try {
        localStorage.setItem(ADMIN_LOGIN_STORAGE_KEY, "1");
        localStorage.setItem(ADMIN_LAST_ACCESS_KEY, String(Date.now()));
      } catch {
        // 失敗しても致命的ではないので握りつぶす
      }

      // 管理トップへ遷移
      navigate("/admin", { replace: true });
    } catch (err) {
      console.error(err);
      setError("通信エラーが発生しました。時間をおいて再度お試しください。");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        bgcolor: "#f5f5f5",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        p: 2,
      }}
    >
      <Paper
        elevation={3}
        sx={{
          width: "100%",
          maxWidth: 420,
          p: 4,
          borderRadius: 3,
        }}
      >
        <Typography variant="h6" component="h1" gutterBottom>
          管理画面ログイン
        </Typography>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          管理者ID とパスワードを入力してください。
        </Typography>

        {error && (
          <Box mt={2}>
            <Alert severity="error">{error}</Alert>
          </Box>
        )}

        <Box component="form" onSubmit={handleSubmit} mt={2}>
          <Stack spacing={2}>
            <TextField
              label="管理者ID"
              size="small"
              fullWidth
              value={id}
              onChange={(e) => setId(e.target.value)}
              autoComplete="username"
            />
            <TextField
              label="パスワード"
              type="password"
              size="small"
              fullWidth
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
            />

            <Button
              type="submit"
              variant="contained"
              fullWidth
              disabled={submitting}
            >
              {submitting ? "ログイン中..." : "ログイン"}
            </Button>
          </Stack>
        </Box>
      </Paper>
    </Box>
  );
};

export default AdminLogin;
